export type OperationEffect = 'enables' | 'restricts' | 'unknown';

/** Datex convention: operation names carry "enable"/"disable" (typically as an
 *  Enable_/Disable_ prefix, e.g. Disable_AsnOrder_Cancel) to signal whether assigning
 *  them turns a feature on or removes it. This is a naming convention, not a schema
 *  guarantee — treat the result as a reading aid, not an authority. */
export function getOperationEffect(operationName: string): OperationEffect {
  const name = (operationName ?? '').toLowerCase();
  if (name.includes('disable')) {
    return 'restricts';
  }
  if (name.includes('enable')) {
    return 'enables';
  }
  return 'unknown';
}

/** Host-own operations are stored with an empty application reference name. */
export function getModuleLabel(applicationReferenceName: string | null): string {
  return applicationReferenceName?.trim() ? applicationReferenceName : 'Host application';
}

export function getOperationKey(applicationReferenceName: string | null, name: string): string {
  return `${applicationReferenceName ?? ''}:${name}`;
}

export interface OperationGrouping<T> {
  moduleLabel: string;
  rows: T[];
}

/** Groups rows by module label, sorting rows by name within each group and
 *  putting the 'Host application' group first, then the rest alphabetically. */
export function groupByModuleLabel<T>(
  rows: T[],
  getGroupLabel: (row: T) => string,
  getName: (row: T) => string
): OperationGrouping<T>[] {
  const groups = new Map<string, T[]>();
  rows.forEach(row => {
    const moduleLabel = getGroupLabel(row);
    if (!groups.has(moduleLabel)) {
      groups.set(moduleLabel, []);
    }
    groups.get(moduleLabel).push(row);
  });
  return [...groups.entries()]
    .map(([moduleLabel, groupRows]) => ({
      moduleLabel,
      rows: groupRows.sort((a, b) => getName(a).localeCompare(getName(b)))
    }))
    .sort((a, b) => a.moduleLabel === 'Host application' ? -1
      : b.moduleLabel === 'Host application' ? 1
      : a.moduleLabel.localeCompare(b.moduleLabel));
}
